import React, { useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { Switch, Loader, Text, Tooltip } from '@mantine/core';
import { GET_STORES } from '../../apollo/queries/store';
import { TOGGLE_STORE_MODULE } from '../../apollo/mutations/store';
import { ModuleType } from '../../codegen/graphql';
import {
    showErrorNotification,
    showSuccessNotification,
} from '../../notifications';
import { PropagationStopper, When } from '../../components/common';

type StoreModuleRow = {
    id: string;
    name?: string | null;
    storeNo?: string | null;
    advertisingZoneId?: string | null;
    isSalesModuleEnabled?: boolean | null;
    isPriceChangeModuleEnabled?: boolean | null;
    isPriceSurveyModuleEnabled?: boolean | null;
    isClosed?: boolean | null;
    isPhysicalStore?: boolean | null;
};

function getStoreModuleSwitchLabel(
    store: StoreModuleRow,
    moduleName: string,
): string {
    const storeNo = store.storeNo?.trim();
    const storeName = store.name?.trim();
    let storeLabel = store.id;
    if (storeNo && storeName) {
        storeLabel = `${storeNo} ${storeName}`;
    } else if (storeName) {
        storeLabel = storeName;
    } else if (storeNo) {
        storeLabel = storeNo;
    }
    return `${moduleName} for ${storeLabel}`;
}

function StoreModuleManager() {
    const [updatingSalesModule, setUpdatingSalesModule] = useState<
        Record<string, boolean>
    >({});
    const [updatingPriceChangeModule, setUpdatingPriceChangeModule] = useState<
        Record<string, boolean>
    >({});
    const [updatingPriceSurveyModule, setUpdatingPriceSurveyModule] = useState<
        Record<string, boolean>
    >({});

    const { data, loading, error } = useQuery(GET_STORES);

    const [toggleStoreModule] = useMutation(TOGGLE_STORE_MODULE, {
        onCompleted: () => {
            showSuccessNotification('Module status updated successfully');
        },
        onError: e => {
            showErrorNotification(
                `Failed to update module status: ${e.message}`,
            );
        },
        refetchQueries: [{ query: GET_STORES }],
        awaitRefetchQueries: true,
    });

    const handleToggleSalesModule = async (
        store: StoreModuleRow,
        enabled: boolean,
    ) => {
        setUpdatingSalesModule(prev => ({ ...prev, [store.id]: true }));
        try {
            await toggleStoreModule({
                variables: {
                    input: {
                        storeId: store.id,
                        module: ModuleType.Sales,
                        enabled,
                    },
                },
            });
        } finally {
            setUpdatingSalesModule(prev => ({ ...prev, [store.id]: false }));
        }
    };

    const handleTogglePriceChangeModule = async (
        store: StoreModuleRow,
        enabled: boolean,
    ) => {
        setUpdatingPriceChangeModule(prev => ({ ...prev, [store.id]: true }));
        try {
            await toggleStoreModule({
                variables: {
                    input: {
                        storeId: store.id,
                        module: ModuleType.PriceChange,
                        enabled,
                    },
                },
            });
        } finally {
            setUpdatingPriceChangeModule(prev => ({
                ...prev,
                [store.id]: false,
            }));
        }
    };

    const handleTogglePriceSurveyModule = async (
        store: StoreModuleRow,
        enabled: boolean,
    ) => {
        setUpdatingPriceSurveyModule(prev => ({ ...prev, [store.id]: true }));
        try {
            await toggleStoreModule({
                variables: {
                    input: {
                        storeId: store.id,
                        module: ModuleType.PriceSurvey,
                        enabled,
                    },
                },
            });
        } finally {
            setUpdatingPriceSurveyModule(prev => ({
                ...prev,
                [store.id]: false,
            }));
        }
    };

    const filteredStores: StoreModuleRow[] =
        data?.getStores.filter((store: StoreModuleRow) => {
            return !store.isClosed && store.isPhysicalStore;
        }) ?? [];

    if (loading) return <Loader />;
    if (error)
        return (
            <span className="text-red-600">
                Error loading stores: {error.message}
            </span>
        );

    return (
        <div className="overflow-x-auto">
            <table className="min-w-full bg-white border border-gray-200 rounded-lg border-separate border-spacing-0">
                <thead>
                    <tr className="bg-gray-50">
                        <th className="px-4 py-3 text-left font-semibold align-middle">
                            Store Number
                        </th>
                        <th className="px-4 py-3 text-left font-semibold align-middle">
                            Store Name
                        </th>
                        <th className="px-4 py-3 text-center font-semibold align-middle whitespace-nowrap">
                            Sales
                        </th>
                        <th className="px-4 py-3 text-center font-semibold align-middle whitespace-nowrap">
                            Price Change
                        </th>
                        <th className="px-4 py-3 text-center font-semibold align-middle whitespace-nowrap">
                            Price Surveys
                        </th>
                    </tr>
                </thead>
                <tbody>
                    {filteredStores.map((store, idx) => {
                        const isZoneless = !store.advertisingZoneId;

                        return (
                            <tr
                                key={store.id}
                                className={
                                    idx % 2 === 0
                                        ? 'bg-white hover:bg-gray-50'
                                        : 'bg-gray-50 hover:bg-gray-100'
                                }
                            >
                                <td className="px-4 py-2 whitespace-nowrap">
                                    {store.storeNo}
                                </td>
                                <td className="px-4 py-2 whitespace-nowrap">
                                    {store.name}
                                </td>
                                <td className="px-4 py-2">
                                    <div className="flex items-center justify-center">
                                        {updatingSalesModule[store.id] ? (
                                            <Loader size="sm" />
                                        ) : (
                                            <Switch
                                                checked={
                                                    !!store.isSalesModuleEnabled
                                                }
                                                onChange={event =>
                                                    handleToggleSalesModule(
                                                        store,
                                                        event.currentTarget
                                                            .checked,
                                                    )
                                                }
                                                color="teal"
                                                size="md"
                                            />
                                        )}
                                    </div>
                                </td>
                                <td className="px-4 py-2">
                                    <div className="flex items-center justify-center">
                                        {updatingPriceChangeModule[store.id] ? (
                                            <Loader size="sm" />
                                        ) : (
                                            <Switch
                                                checked={
                                                    !!store.isPriceChangeModuleEnabled
                                                }
                                                onChange={event =>
                                                    handleTogglePriceChangeModule(
                                                        store,
                                                        event.currentTarget
                                                            .checked,
                                                    )
                                                }
                                                color="blue"
                                                size="md"
                                            />
                                        )}
                                    </div>
                                </td>
                                <td className="px-4 py-2">
                                    <div className="flex items-center justify-center">
                                        <PropagationStopper>
                                            {updatingPriceSurveyModule[
                                                store.id
                                            ] ? (
                                                <Loader size="sm" />
                                            ) : (
                                                <When
                                                    condition={isZoneless}
                                                    fallback={
                                                        <Tooltip label="Controlled by the store's advertising zone. Use Advertising Zone Modules.">
                                                            <Text
                                                                size="sm"
                                                                c="dimmed"
                                                                className="cursor-help"
                                                            >
                                                                Via zone
                                                            </Text>
                                                        </Tooltip>
                                                    }
                                                >
                                                    <Switch
                                                        checked={
                                                            !!store.isPriceSurveyModuleEnabled
                                                        }
                                                        onChange={event =>
                                                            handleTogglePriceSurveyModule(
                                                                store,
                                                                event
                                                                    .currentTarget
                                                                    .checked,
                                                            )
                                                        }
                                                        color="orange"
                                                        size="md"
                                                        aria-label={getStoreModuleSwitchLabel(
                                                            store,
                                                            'Price Surveys',
                                                        )}
                                                    />
                                                </When>
                                            )}
                                        </PropagationStopper>
                                    </div>
                                </td>
                            </tr>
                        );
                    })}
                    {filteredStores.length === 0 && (
                        <tr>
                            <td
                                colSpan={5}
                                className="text-center py-4 text-gray-500"
                            >
                                No stores found
                            </td>
                        </tr>
                    )}
                </tbody>
            </table>
        </div>
    );
}

export default StoreModuleManager;
